import { NOT_SENT_REASON_HE } from '@nw/shared';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Sev } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { errorMessage } from '../../lib/api';
import { useRealtime } from '../../lib/realtime';
import { useThrottledInvalidate } from '../../lib/use-throttled-invalidate';
import { useCan } from '../../lib/session';
import { ConfirmModal } from '../B1/shared';
import { b5Keys, useAlerts, useSendAnyway, type AlertItem, type AlertStatus } from './api';
import { fmtDateTime, fmtTime } from './editor';
import { sendActionOf } from './send-action';

/** "התראות אחרונות" panel in the client alerts tab: the alert log (GET /api/alerts), newest first, with ✅ acks. */

export const ALERT_FILTERS = ['', 'open', 'sent', 'skipped', 'failed', 'off'] as const;
export type AlertFilter = (typeof ALERT_FILTERS)[number];
const FILTER_LABEL: Record<AlertFilter, string> = { '': 'הכל', open: 'לא טופלו', sent: 'נשלחו', skipped: 'לא נשלחו', failed: 'נכשלו', off: 'התראות כבויות' };

const STATUS: Record<AlertStatus, [string, '' | 'ok' | 'info' | 'warn' | 'danger']> = {
  pending: ['ממתינה', 'info'],
  queued: ['בתור לשליחה', 'info'],
  batched: ['בהודעה מקובצת', 'info'],
  sent: ['נשלחה', 'ok'],
  skipped_stale: ['לא נשלחה', ''],
  skipped_rule: ['לא נשלחה', ''],
  skipped_inactive: ['לא נשלחה', ''],
  skipped_no_route: ['לא נשלחה', 'warn'],
  failed: ['נכשלה', 'danger'],
};

/** W3-2-1: the not-sent reasons live in @nw/shared (the portal and the staff inbox use the same map) */
/** W2-6-9 name of the same map (the staff inbox imports it from here) */
export const ALERT_REASON_HE = NOT_SENT_REASON_HE;
/** D-122: the one retro message per client is stored under this pseudo rule id (never a rule) */
const RETRO_REPORT_RULE = 'retro-report';
const ruleLabel = (a: AlertItem) => (a.ruleUuid === RETRO_REPORT_RULE ? 'הודעת השלמת נתונים' : (a.ruleName ?? a.ruleUuid));
const reasonText = (r: string | null) => (r ? (ALERT_REASON_HE[r] ?? (r.startsWith('outbox_blocked') ? 'נחסמה בשליחה' : r)) : '');
const href = (u: string) => (/^https?:\/\//.test(u) ? u : `https://${u}`);

export function AlertsList(props: { clientId: string; filter: AlertFilter; onFilter: (f: AlertFilter) => void }) {
  const qc = useQueryClient();
  const q = useAlerts(props.clientId, props.filter);
  const can = useCan();
  const sendAnyway = useSendAnyway();
  const [pending, setPending] = useState<AlertItem | null>(null);
  const runSendAnyway = (a: AlertItem) => {
    sendAnyway.mutate(a.id, {
      onSuccess: (r) => {
        const [label] = STATUS[r.status] ?? [r.status];
        const skipped = r.status.startsWith('skipped_') || r.status === 'failed';
        toast(skipped ? `לא נשלחה: ${r.reasonDetail ?? reasonText(r.reason)}` : `ההתראה יצאה לשליחה (${label})`, skipped ? 'error' : 'ok');
      },
      onSettled: () => setPending(null),
    });
  };
  // asks first (a Modal, never window.confirm); "שלח בכל זאת" (old call / rule off) or "שלח שוב" (cause fixed / send failed)
  const onSendAnyway = (a: AlertItem) => setPending(a);
  const pendingAction = pending ? sendActionOf(pending) : null;

  // Alert status follows the outbox message (sent/failed), so refresh on outbox pushes.
  const onOutbox = useThrottledInvalidate([b5Keys.alertsAll], 1000);
  useRealtime('outbox.updated', onOutbox);
  // Decision / status / ✅ ack of this client's alerts (pipeline pushes 'alert.updated').
  const onAlert = useCallback(
    (p: { clientId: string }) => {
      if (p.clientId === props.clientId) void qc.invalidateQueries({ queryKey: b5Keys.alertsAll });
    },
    [qc, props.clientId],
  );
  useRealtime('alert.updated', onAlert);

  useEffect(() => {
    if (q.error) toast(errorMessage(q.error), 'error');
  }, [q.error]);

  const items: AlertItem[] = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = q.data?.pages[0]?.total;

  return (
    <div className="panel flush mt">
      <div className="panel-head">
        <h2>התראות אחרונות</h2>
        {total != null ? <span className="pill">{total}</span> : null}
        <div className="actions">
          <div className="seg-ctrl" role="group" aria-label="סינון התראות">
            {ALERT_FILTERS.map((f) => (
              <button key={f || 'all'} type="button" aria-pressed={props.filter === f} onClick={() => props.onFilter(f)}>
                {FILTER_LABEL[f]}
              </button>
            ))}
          </div>
        </div>
        <span className="hint">כל התראה שהגיעה מ-NIVISION ללקוח הזה: אם נשלחה, ואם מישהו בקבוצה סימן ✅.</span>
      </div>
      {q.isLoading ? (
        <div className="panel-body stack" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skel" style={{ height: 34 }} />
          ))}
        </div>
      ) : q.error ? (
        <div className="panel-body">
          <div className="callout danger">
            <Icon name="alert" />
            <div>
              <b>לא הצלחנו לטעון את ההתראות.</b> {errorMessage(q.error)}
            </div>
            <div className="callout-actions">
              <button className="btn sm" onClick={() => void q.refetch()}>
                נסו שוב
              </button>
            </div>
          </div>
        </div>
      ) : !items.length ? (
        <div className="empty">
          <div className="art">
            <Icon name="bolt" />
          </div>
          <h2>{props.filter ? 'אין התראות בסינון הזה' : 'עוד אין התראות'}</h2>
          <p>
            {props.filter
              ? 'אפשר לבחור "הכל" כדי לראות את כל ההתראות.'
              : 'כשתגיע מ-NIVISION שיחה עם התראה, היא תופיע כאן עם הסטטוס ומי סימן ✅.'}
          </p>
          {!props.filter ? (
            <Link className="btn sm" to="/client/$clientId/simulation" params={{ clientId: props.clientId }}>
              לבדיקה בהדמיה
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>מתי</th>
                  <th>התראה</th>
                  <th>סטטוס</th>
                  <th className="hide-sm">✅ טופל</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const [label, tone] = STATUS[a.status] ?? [a.status, ''];
                  const action = can('alerts.edit') ? sendActionOf(a) : null;
                  return (
                    <tr key={a.id}>
                      <td>
                        <div className="small num">{fmtDateTime(a.callStartedAt ?? a.createdAt)}</div>
                      </td>
                      <td>
                        <div className="cell-title">
                          <Sev level={a.severity} /> {ruleLabel(a)}
                        </div>
                        <div className="cell-sub">{[a.agentName, a.siteName].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td>
                        <span className={`pill ${tone}`}>
                          <span className="d" />
                          {label}
                        </span>
                        {a.reason ? <div className="cell-sub b5-reason">{a.reasonDetail ?? reasonText(a.reason)}</div> : null}
                      </td>
                      <td className="hide-sm">
                        {a.ack ? (
                          <span className="b5-ack">
                            ✅ {a.ack.name ?? 'סומן'} {fmtTime(a.ack.at)}
                          </span>
                        ) : (
                          <span className="faint small">-</span>
                        )}
                      </td>
                      <td className="actions">
                        <span className="b5-acts">
                          {action ? (
                            <button
                              type="button"
                              className="btn sm"
                              disabled={sendAnyway.isPending && sendAnyway.variables === a.id}
                              onClick={() => onSendAnyway(a)}
                            >
                              {sendAnyway.isPending && sendAnyway.variables === a.id ? 'שולח…' : action.label}
                            </button>
                          ) : a.status === 'failed' && a.sendRetrying ? (
                            <span className="faint small">השליחה עדיין מנסה שוב</span>
                          ) : null}
                          {a.reportUrl ? (
                            <a className="link small" href={href(a.reportUrl)} target="_blank" rel="noreferrer noopener">
                              לדוח
                            </a>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {q.hasNextPage ? (
            <div className="b5-more">
              <button className="btn sm" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
                {q.isFetchingNextPage ? 'טוען…' : 'עוד התראות'}
              </button>
            </div>
          ) : null}
        </>
      )}
      <ConfirmModal
        open={Boolean(pending)}
        title={pendingAction?.title ?? ''}
        confirmLabel={pendingAction?.label ?? ''}
        tone="primary"
        pending={sendAnyway.isPending}
        onConfirm={() => pending && runSendAnyway(pending)}
        onClose={() => setPending(null)}
      >
        <p>{pendingAction?.confirm}</p>
      </ConfirmModal>
    </div>
  );
}
